import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { 
  Calendar, 
  Plus, 
  Edit, 
  Trash2, 
  GripVertical,
  Loader2,
  Save,
  Check,
  X,
  ArrowLeft,
  Bell,
  Clock,
  Palette,
  MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Predefined colors for appointment types
const COLOR_OPTIONS = [
  { value: '#3b82f6', label: 'Blue' },
  { value: '#22c55e', label: 'Green' },
  { value: '#a855f7', label: 'Purple' },
  { value: '#f97316', label: 'Orange' },
  { value: '#14b8a6', label: 'Teal' },
  { value: '#6366f1', label: 'Indigo' },
  { value: '#ef4444', label: 'Red' },
  { value: '#eab308', label: 'Yellow' },
  { value: '#ec4899', label: 'Pink' },
  { value: '#6b7280', label: 'Gray' }
];

const AppointmentSettingsPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [appointmentTypes, setAppointmentTypes] = useState([]);
  
  // SNAP E&T Appointment Reminder state
  const [appointmentReminderSettings, setAppointmentReminderSettings] = useState(null);
  const [appointmentReminderMessage, setAppointmentReminderMessage] = useState('');
  const [appointmentReminderEnabled, setAppointmentReminderEnabled] = useState(true);
  const [savingAppointmentReminder, setSavingAppointmentReminder] = useState(false);
  
  // Dialog state
  const [showTypeDialog, setShowTypeDialog] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [typeForm, setTypeForm] = useState({
    name: '',
    description: '',
    default_duration_minutes: 30,
    color: '#6b7280',
    triggers_snap_et_reminder: false
  });

  const isAdmin = user?.role === 'system_admin' || user?.role === 'executive';

  const fetchAppointmentTypes = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/appointment-types`);
      setAppointmentTypes(response.data.appointment_types || []);
    } catch (error) {
      console.error('Failed to fetch appointment types:', error);
      toast.error('Failed to load appointment types');
    }
  }, []);

  const fetchAppointmentReminderSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/settings/snap-et-appointment-reminder`);
      setAppointmentReminderSettings(response.data);
      setAppointmentReminderMessage(response.data.current_message || '');
      setAppointmentReminderEnabled(response.data.enabled !== false);
    } catch (error) {
      console.error('Failed to fetch appointment reminder settings:', error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchAppointmentTypes(),
        fetchAppointmentReminderSettings()
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchAppointmentTypes, fetchAppointmentReminderSettings]);

  const handleSaveAppointmentReminder = async () => {
    setSavingAppointmentReminder(true);
    try {
      await axios.put(`${API}/settings/snap-et-appointment-reminder`, {
        message: appointmentReminderMessage,
        enabled: appointmentReminderEnabled
      });
      toast.success('Appointment reminder settings updated');
      fetchAppointmentReminderSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update appointment reminder settings');
    } finally {
      setSavingAppointmentReminder(false);
    }
  };

  const handleResetAppointmentReminderMessage = () => {
    if (appointmentReminderSettings?.default_message) {
      setAppointmentReminderMessage(appointmentReminderSettings.default_message);
    }
  };

  const openAddDialog = () => {
    setEditingType(null);
    setTypeForm({
      name: '',
      description: '',
      default_duration_minutes: 30,
      color: '#6b7280',
      triggers_snap_et_reminder: false
    });
    setShowTypeDialog(true);
  };

  const openEditDialog = (type) => {
    setEditingType(type);
    setTypeForm({
      name: type.name,
      description: type.description || '',
      default_duration_minutes: type.default_duration_minutes || 30,
      color: type.color || '#6b7280',
      triggers_snap_et_reminder: type.triggers_snap_et_reminder || false
    });
    setShowTypeDialog(true);
  };

  const handleSaveType = async () => {
    if (!typeForm.name.trim()) {
      toast.error('Name is required');
      return;
    }

    setSaving(true);
    try {
      if (editingType) {
        await axios.put(`${API}/admin/appointment-types/${editingType.id}`, typeForm);
        toast.success('Appointment type updated');
      } else {
        await axios.post(`${API}/admin/appointment-types`, typeForm);
        toast.success('Appointment type created');
      }
      setShowTypeDialog(false);
      fetchAppointmentTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save appointment type');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (type) => {
    try {
      const response = await axios.post(`${API}/admin/appointment-types/${type.id}/toggle-status`);
      toast.success(response.data.message);
      fetchAppointmentTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleReorder = async (dragIndex, dropIndex) => {
    if (dragIndex === dropIndex) return;

    const newOrder = [...appointmentTypes];
    const [draggedItem] = newOrder.splice(dragIndex, 1);
    newOrder.splice(dropIndex, 0, draggedItem);

    // Optimistic update
    setAppointmentTypes(newOrder);

    try {
      await axios.post(`${API}/admin/appointment-types/reorder`, {
        order: newOrder.map(t => t.id)
      });
      toast.success('Order updated');
    } catch (error) {
      toast.error('Failed to reorder');
      fetchAppointmentTypes();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <p className="text-red-600">You don't have permission to access this page.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/settings">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Settings
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Calendar className="w-6 h-6 text-primary" />
              Appointment Settings
            </h1>
            <p className="text-muted-foreground">Manage appointment types, durations, and reminder settings</p>
          </div>
        </div>
      </div>

      {/* Appointment Types Section */}
      <Card className="card-shadow">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Appointment Types
              </CardTitle>
              <CardDescription>
                Configure the types of appointments available for scheduling. Drag to reorder.
              </CardDescription>
            </div>
            <Button onClick={openAddDialog} data-testid="add-appointment-type-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Type
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {appointmentTypes.map((type, index) => (
              <div
                key={type.id}
                className={`flex items-center gap-4 p-4 border rounded-lg ${
                  !type.is_active ? 'bg-gray-50 opacity-60' : 'bg-white'
                }`}
                data-testid={`appointment-type-${type.id}`}
              >
                <div className="cursor-move text-gray-400 hover:text-gray-600">
                  <GripVertical className="w-5 h-5" />
                </div>
                
                <div
                  className="w-4 h-4 rounded-full flex-shrink-0"
                  style={{ backgroundColor: type.color || '#6b7280' }}
                />
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{type.name}</span>
                    {!type.is_active && (
                      <Badge variant="secondary" className="text-xs">Inactive</Badge>
                    )}
                    {type.triggers_snap_et_reminder && (
                      <Badge variant="outline" className="text-xs text-green-600 border-green-600">
                        <Bell className="w-3 h-3 mr-1" />
                        SMS Reminder
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground truncate">
                    {type.description || 'No description'} • {type.default_duration_minutes || 30} min
                  </p>
                </div>
                
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openEditDialog(type)}
                    data-testid={`edit-type-${type.id}`}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Switch
                    checked={type.is_active !== false}
                    onCheckedChange={() => handleToggleStatus(type)}
                    data-testid={`toggle-type-${type.id}`}
                  />
                </div>
              </div>
            ))}
            
            {appointmentTypes.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No appointment types configured</p>
                <p className="text-sm">Click "Add Type" to create your first appointment type</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* SNAP E&T Appointment Reminder Settings */}
      <Card className="card-shadow">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            SNAP E&T Appointment Reminder Settings
          </CardTitle>
          <CardDescription>
            Configure the 24-hour SMS reminder sent automatically before appointments with reminder triggers enabled
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Enable/Disable Toggle */}
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div>
              <p className="font-medium">Enable 24-Hour SMS Reminders</p>
              <p className="text-sm text-muted-foreground">
                Automatically send SMS reminders 24 hours before appointments with SMS reminder trigger enabled
              </p>
            </div>
            <Switch
              checked={appointmentReminderEnabled}
              onCheckedChange={setAppointmentReminderEnabled}
              data-testid="appointment-reminder-enabled"
            />
          </div>

          {/* Reminder Message Configuration */}
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold mb-2">Reminder SMS Message</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Customize the message sent to participants. Use merge field tokens to include dynamic information.
              </p>
            </div>

            <div className="space-y-3 p-4 border rounded-lg">
              <div className="flex items-center justify-between">
                <Label className="text-base font-medium">Appointment Reminder Message</Label>
                {appointmentReminderSettings?.is_default === false && (
                  <Badge variant="outline" className="text-green-600 border-green-600">Customized</Badge>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2 p-2 bg-purple-50 rounded text-sm">
                <span className="text-purple-700 font-medium">Available Tokens:</span>
                <code className="px-2 py-0.5 bg-purple-100 rounded text-purple-800">[Participant First Name]</code>
                <code className="px-2 py-0.5 bg-purple-100 rounded text-purple-800">[Assigned Case Manager]</code>
                <code className="px-2 py-0.5 bg-purple-100 rounded text-purple-800">[Appointment Date]</code>
                <code className="px-2 py-0.5 bg-purple-100 rounded text-purple-800">[Appointment Time]</code>
              </div>
              <Textarea
                value={appointmentReminderMessage}
                onChange={(e) => setAppointmentReminderMessage(e.target.value)}
                placeholder="Enter the reminder message..."
                rows={5}
                className="resize-none"
                data-testid="appointment-reminder-message"
              />
              <div className="flex justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleResetAppointmentReminderMessage}
                  className="text-muted-foreground"
                >
                  Reset to Default
                </Button>
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end pt-2">
              <Button
                onClick={handleSaveAppointmentReminder}
                disabled={savingAppointmentReminder}
                data-testid="save-appointment-reminder-btn"
              >
                {savingAppointmentReminder ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save Reminder Settings
              </Button>
            </div>
          </div>

          {/* Info about how reminders work */}
          <div className="p-4 border border-green-200 rounded-lg bg-green-50">
            <h4 className="font-medium text-green-900 mb-2">How Appointment Reminders Work</h4>
            <ul className="text-sm text-green-800 space-y-1 list-disc list-inside">
              <li>SMS reminders are sent 24 hours before appointments that have "SMS Reminder" enabled on their type</li>
              <li>The outbound phone number is selected based on the participant's address</li>
              <li>Participant replies are routed to the assigned Case Manager's HELP Messages inbox</li>
              <li>Case managers receive email notifications when participants reply to reminders</li>
              <li>A Case Note is automatically created when each reminder is sent</li>
              <li>Recurring appointments receive reminders before each occurrence</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Add/Edit Type Dialog */}
      <Dialog open={showTypeDialog} onOpenChange={setShowTypeDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingType ? 'Edit Appointment Type' : 'Add Appointment Type'}
            </DialogTitle>
            <DialogDescription>
              {editingType 
                ? 'Update the appointment type details below.' 
                : 'Create a new appointment type for scheduling.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={typeForm.name}
                onChange={(e) => setTypeForm({ ...typeForm, name: e.target.value })}
                placeholder="e.g., Follow-Up Meeting"
                data-testid="type-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                value={typeForm.description}
                onChange={(e) => setTypeForm({ ...typeForm, description: e.target.value })}
                placeholder="Brief description of this appointment type"
                data-testid="type-description-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="duration">Default Duration (minutes)</Label>
              <Input
                id="duration"
                type="number"
                min="5"
                max="480"
                value={typeForm.default_duration_minutes}
                onChange={(e) => setTypeForm({ ...typeForm, default_duration_minutes: parseInt(e.target.value) || 30 })}
                data-testid="type-duration-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Color</Label>
              <div className="flex flex-wrap gap-2">
                {COLOR_OPTIONS.map((color) => (
                  <button
                    key={color.value}
                    type="button"
                    className={`w-8 h-8 rounded-full border-2 transition-all ${
                      typeForm.color === color.value 
                        ? 'border-gray-900 scale-110' 
                        : 'border-transparent hover:scale-105'
                    }`}
                    style={{ backgroundColor: color.value }}
                    onClick={() => setTypeForm({ ...typeForm, color: color.value })}
                    title={color.label}
                    data-testid={`color-${color.label.toLowerCase()}`}
                  />
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between p-4 border rounded-lg bg-amber-50 border-amber-200">
              <div>
                <p className="font-medium text-amber-900">SNAP E&T SMS Reminder Trigger</p>
                <p className="text-sm text-amber-700">
                  When enabled, 24-hour SMS reminders will be sent for SNAP E&T appointments of this type
                </p>
              </div>
              <Switch
                checked={typeForm.triggers_snap_et_reminder}
                onCheckedChange={(checked) => setTypeForm({ ...typeForm, triggers_snap_et_reminder: checked })}
                data-testid="triggers-reminder-toggle"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTypeDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveType} disabled={saving} data-testid="save-type-btn">
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              {editingType ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AppointmentSettingsPage;
